import React, { useState, useEffect } from "react";
import api from "../../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function ModifyCart({
  productId,
  initialCouleur,
  initialTaille,
  initialQuantity = 1,
  productVariantes = [],
  onUpdated,
}) {
  const navigate = useNavigate();
  const normalize = (str) => (str ? str.toString().toLowerCase().trim() : "");

  const [isEditing, setIsEditing] = useState(false);

  const [prevCouleur, setPrevCouleur] = useState(normalize(initialCouleur) || "");
  const [prevTaille, setPrevTaille] = useState(normalize(initialTaille) || "");

  const [newCouleur, setNewCouleur] = useState(normalize(initialCouleur) || "");
  const [newTaille, setNewTaille] = useState(normalize(initialTaille) || "");
  const [quantity, setQuantity] = useState(initialQuantity || 1);
  const [loading, setLoading] = useState(false);

  // Trouver la variante correspondant à la couleur sélectionnée (en minuscules)
  const productStock =
    productVariantes.find((v) => normalize(v.couleur) === newCouleur)?.stock || [];

  // Trouver la quantité max disponible sur la nouvelle taille
  const maxStockForSize = productStock.find((s) => normalize(s.taille) === newTaille)?.quantity ?? 1;

  // Gestion changement quantité avec min 1 et max stock disponible
  const handleQuantityChange = (newQty) => {
    if (newQty < 1) {
      setQuantity(1);
      return;
    }
    if (newQty > maxStockForSize) {
      setQuantity(maxStockForSize);
      toast.warning(`Quantité maximale disponible : ${maxStockForSize}`);
      return;
    }
    setQuantity(newQty);
  };

  // Mise à jour du panier
  const handleUpdate = async () => {
    if (quantity > maxStockForSize) {
      toast.error(`Quantité maximale disponible pour cette taille et couleur : ${maxStockForSize}`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        productId,
        taille: prevTaille,
        couleur: prevCouleur,
        newTaille,
        newCouleur,
        quantity,
      };

      console.log("Payload update cart:", payload);

              await api.put(`/api/shop/cart/update`, payload, {
        withCredentials: true,
      });

      toast.success("Panier mis à jour !", {
        onClick: () => navigate('/cart'),
        style: { cursor: 'pointer' }
      });
      setIsEditing(false);

      setPrevCouleur(newCouleur);
      setPrevTaille(newTaille);

      if (typeof onUpdated === "function") {
        onUpdated({ couleur: newCouleur, taille: newTaille, quantity });
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour du panier.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setQuantity(initialQuantity || 1);
    setNewCouleur(prevCouleur);
    setNewTaille(prevTaille);
  };

  // Quand on change de couleur, reset taille à la première dispo si la taille actuelle n'existe pas
  useEffect(() => {
    if (productStock.length > 0) {
      const tailleExists = productStock.some(
        (stockItem) => normalize(stockItem.taille) === newTaille
      );
      if (!tailleExists) {
        setNewTaille(normalize(productStock[0].taille));
      }
    } else {
      setNewTaille("");
    }
    // Reset quantity à 1 ou max stock dispo quand couleur ou taille change
    setQuantity(1);
  }, [newCouleur]);

  // Quand on change de taille, ajuster la quantité max si besoin
  useEffect(() => {
    if (quantity > maxStockForSize) {
      setQuantity(maxStockForSize);
    }
  }, [newTaille, maxStockForSize]);

  return (
    <div style={styles.container}>
      {!isEditing ? (
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsEditing(true)}
          style={styles.editButton}
        >
          Modifier
        </motion.button>
      ) : (
        <>
          {/* Sélecteur de couleur */}
          <div style={styles.colorsContainer}>
            {productVariantes.length > 0 ? (
              productVariantes.map(({ couleur }) => {
                const normalizedCouleur = normalize(couleur);
                const isSelected = newCouleur === normalizedCouleur;
                return (
                  <motion.button
                    key={couleur}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setNewCouleur(normalizedCouleur)}
                    style={{
                      ...styles.colorButton,
                      ...(isSelected ? styles.colorButtonSelected : {}),
                    }}
                    type="button"
                  >
                    {couleur}
                  </motion.button>
                );
              })
            ) : (
              <em style={styles.noColorText}>Aucune couleur disponible</em>
            )}
          </div>

          {/* Sélecteur de taille */}
          <div style={styles.sizesContainer}>
            {productStock.length > 0 ? (
              productStock.map(({ taille, quantity: stockQty }) => {
                const normalizedTaille = normalize(taille);
                const isSelected = newTaille === normalizedTaille;
                const isOutOfStock = stockQty === 0;

                return (
                  <motion.button
                    key={taille}
                    whileHover={!isOutOfStock ? { scale: 1.05 } : {}}
                    whileTap={!isOutOfStock ? { scale: 0.95 } : {}}
                    onClick={() => !isOutOfStock && setNewTaille(normalizedTaille)}
                    disabled={isOutOfStock}
                    style={{
                      ...styles.sizeButton,
                      ...(isSelected ? styles.sizeButtonSelected : {}),
                      ...(isOutOfStock ? styles.sizeButtonDisabled : {}),
                    }}
                    type="button"
                  >
                    {taille}
                    {isOutOfStock && <div style={styles.stockBadge}>RUPTURE</div>}
                  </motion.button>
                );
              })
            ) : (
              <em style={styles.noSizeText}>Aucune taille disponible</em>
            )}
          </div>

          {/* Sélecteur de quantité amélioré */}
          <div style={styles.quantitySection}>
            <div style={styles.quantityLabel}>
              <span style={styles.quantityText}>Quantité</span>
              <span style={styles.stockInfo}>
                {maxStockForSize > 1 ? `${maxStockForSize} disponibles` : '1 disponible'}
              </span>
            </div>
            
            <div style={styles.quantitySelector}>
              <motion.button
                whileHover={{ 
                  backgroundColor: quantity <= 1 ? "rgba(158, 158, 158, 0.2)" : "rgba(212, 175, 55, 0.2)",
                  scale: quantity <= 1 ? 1 : 1.05
                }}
                whileTap={{ scale: quantity <= 1 ? 1 : 0.95 }}
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= 1}
                style={{
                  ...styles.quantityButton,
                  ...(quantity <= 1 ? styles.quantityButtonDisabled : styles.quantityButtonActive),
                }}
                type="button"
              >
                <span style={styles.quantityIcon}>−</span>
              </motion.button>
              
              <div style={styles.quantityDisplay}>
                <input
                  type="number"
                  min="1"
                  max={maxStockForSize}
                  value={quantity}
                                onChange={(e) => {
                let val = parseInt(e.target.value, 10);
                if (isNaN(val) || val < 1) {
                  val = 1;
                  setQuantity(val);
                } else if (val > maxStockForSize) {
                  val = maxStockForSize;
                  setQuantity(val);
                  toast.warning(`Quantité maximale disponible : ${maxStockForSize}`);
                } else {
                  setQuantity(val);
                }
              }}
                  onBlur={(e) => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val) || val < 1) {
                      setQuantity(1);
                      e.target.value = "1";
                      toast.info("La quantité minimale pour commander est de 1 exemplaire");
                    } else if (val > maxStockForSize) {
                      setQuantity(maxStockForSize);
                      e.target.value = maxStockForSize.toString();
                      toast.warning(`Quantité maximale disponible : ${maxStockForSize}`);
                    }
                  }}
                  style={styles.quantityInput}
                />
                <span style={styles.quantityMax}>/ {maxStockForSize}</span>
              </div>
              
              <motion.button
                whileHover={{ 
                  backgroundColor: quantity >= maxStockForSize ? "rgba(158, 158, 158, 0.2)" : "rgba(212, 175, 55, 0.2)",
                  scale: quantity >= maxStockForSize ? 1 : 1.05
                }}
                whileTap={{ scale: quantity >= maxStockForSize ? 1 : 0.95 }}
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={quantity >= maxStockForSize}
                style={{
                  ...styles.quantityButton,
                  ...(quantity >= maxStockForSize ? styles.quantityButtonDisabled : styles.quantityButtonActive),
                }}
                type="button"
              >
                <span style={styles.quantityIcon}>+</span>
              </motion.button>
            </div>
            
            {quantity >= maxStockForSize && maxStockForSize > 1 && (
              <div style={styles.stockWarning}>
                <span style={styles.warningIcon}>⚠️</span>
                Quantité maximale atteinte
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div style={styles.actionButtons}>
            <motion.button
              whileHover={{ backgroundColor: "#333333" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleUpdate}
              disabled={loading}
              style={{
                ...styles.validateButton,
                ...(loading ? styles.loadingButton : {}),
              }}
              type="button"
            >
              {loading ? (
                <div style={styles.loadingDots}>
                  <div style={styles.loadingDot}></div>
                  <div style={styles.loadingDot}></div>
                  <div style={styles.loadingDot}></div>
                </div>
              ) : (
                "Valider"
              )}
            </motion.button>

            <motion.button
              whileHover={{ backgroundColor: "#333333" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleCancel}
              style={styles.cancelButton}
              type="button"
            >
              Annuler
            </motion.button>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    marginTop: "16px",
    padding: "16px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  },
  editButton: {
    padding: "12px 20px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(20px)",
    color: "#5a3921",
    border: "1px solid rgba(139, 90, 43, 0.3)",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
    transition: "all 0.3s ease",
    fontFamily: "'Cormorant Garamond', serif",
    boxShadow: "0 4px 15px rgba(139, 90, 43, 0.1)",
    position: "relative",
    overflow: "hidden",
  },
  colorsContainer: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  colorButton: {
    padding: "10px 16px",
    borderRadius: "10px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(139, 90, 43, 0.2)",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#5a3921",
    transition: "all 0.3s ease",
    fontFamily: "'Cormorant Garamond', serif",
    boxShadow: "0 2px 8px rgba(139, 90, 43, 0.1)",
  },
  colorButtonSelected: {
    backgroundColor: "rgba(139, 90, 43, 0.2)",
    color: "#5a3921",
    borderColor: "rgba(139, 90, 43, 0.5)",
    boxShadow: "0 4px 15px rgba(139, 90, 43, 0.2)",
  },
  sizesContainer: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  sizeButton: {
    position: "relative",
    padding: "10px 14px",
    borderRadius: "10px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(139, 90, 43, 0.2)",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#5a3921",
    transition: "all 0.3s ease",
    minWidth: "50px",
    textAlign: "center",
    overflow: "hidden",
    fontFamily: "'Cormorant Garamond', serif",
    boxShadow: "0 2px 8px rgba(139, 90, 43, 0.1)",
  },
  sizeButtonSelected: {
    backgroundColor: "rgba(139, 90, 43, 0.2)",
    color: "#5a3921",
    borderColor: "rgba(139, 90, 43, 0.5)",
    boxShadow: "0 4px 15px rgba(139, 90, 43, 0.2)",
  },
  sizeButtonDisabled: {
    backgroundColor: "rgba(245, 245, 245, 0.8)",
    color: "#9e9e9e",
    cursor: "not-allowed",
    borderColor: "rgba(158, 158, 158, 0.3)",
  },
  stockBadge: {
    position: "absolute",
    bottom: "0",
    left: "0",
    right: "0",
    fontSize: "10px",
    fontWeight: "bold",
    color: "#fff",
    backgroundColor: "#f44336",
    padding: "2px",
    transform: "translateY(100%)",
  },
  noSizeText: {
    color: "#757575",
    fontStyle: "normal",
    fontSize: "14px",
  },
  noColorText: {
    color: "#757575",
    fontStyle: "normal",
    fontSize: "14px",
  },
  quantitySection: {
    marginBottom: "20px",
  },
  quantityLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  quantityText: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2c1810",
    fontFamily: "'Cormorant Garamond', serif",
  },
  stockInfo: {
    fontSize: "12px",
    color: "#d4af37",
    fontWeight: "500",
    fontFamily: "'Cormorant Garamond', serif",
  },
  quantitySelector: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "8px",
    borderRadius: "15px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(212, 175, 55, 0.2)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
  },
  quantityButton: {
    padding: "12px 16px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(212, 175, 55, 0.3)",
    borderRadius: "12px",
    fontSize: "18px",
    cursor: "pointer",
    color: "#2c1810",
    minWidth: "48px",
    textAlign: "center",
    transition: "all 0.3s ease",
    fontFamily: "'Cormorant Garamond', serif",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  quantityButtonActive: {
    backgroundColor: "rgba(212, 175, 55, 0.1)",
    borderColor: "rgba(212, 175, 55, 0.5)",
    boxShadow: "0 4px 15px rgba(212, 175, 55, 0.2)",
  },
  quantityButtonDisabled: {
    backgroundColor: "rgba(158, 158, 158, 0.1)",
    color: "#9e9e9e",
    cursor: "not-allowed",
    borderColor: "rgba(158, 158, 158, 0.3)",
    boxShadow: "none",
  },
  quantityDisplay: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 12px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(212, 175, 55, 0.3)",
    borderRadius: "10px",
    minWidth: "80px",
  },
  quantityInput: {
    width: "40px",
    padding: "4px",
    textAlign: "center",
    border: "none",
    outline: "none",
    fontSize: "16px",
    fontWeight: "600",
    backgroundColor: "transparent",
    fontFamily: "'Cormorant Garamond', serif",
    color: "#2c1810",
  },
  quantityMax: {
    fontSize: "12px",
    color: "#9e9e9e",
    fontWeight: "500",
    fontFamily: "'Cormorant Garamond', serif",
  },
  quantityIcon: {
    fontSize: "20px",
    fontWeight: "600",
    lineHeight: "1",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
  },
  validateButton: {
    padding: "12px 20px",
    backgroundColor: "rgba(139, 90, 43, 0.1)",
    backdropFilter: "blur(20px)",
    color: "#5a3921",
    border: "1px solid rgba(139, 90, 43, 0.3)",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
    flex: 1,
    transition: "all 0.3s ease",
    fontFamily: "'Cormorant Garamond', serif",
    boxShadow: "0 4px 15px rgba(139, 90, 43, 0.1)",
  },
  cancelButton: {
    padding: "12px 20px",
    backgroundColor: "rgba(108, 117, 125, 0.1)",
    backdropFilter: "blur(20px)",
    color: "#6c757d",
    border: "1px solid rgba(108, 117, 125, 0.3)",
    borderRadius: "12px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
    flex: 1,
    transition: "all 0.3s ease",
    fontFamily: "'Cormorant Garamond', serif",
    boxShadow: "0 4px 15px rgba(108, 117, 125, 0.1)",
  },
  loadingButton: {
    cursor: "not-allowed",
  },
  loadingDots: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    height: "20px",
  },
  loadingDot: {
    width: "6px",
    height: "6px",
    backgroundColor: "#fff",
    borderRadius: "50%",
    animation: "bounce 1.4s infinite ease-in-out",
  },
  stockWarning: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "8px",
    padding: "8px 12px",
    backgroundColor: "rgba(255, 193, 7, 0.1)",
    border: "1px solid rgba(255, 193, 7, 0.3)",
    borderRadius: "8px",
    fontSize: "12px",
    color: "#f57c00",
    fontWeight: "500",
    fontFamily: "'Cormorant Garamond', serif",
  },
  warningIcon: {
    fontSize: "14px",
  },
};

/* Ajoute dans ton CSS global ou dans un styled-component la définition de l'animation bounce */
